import L, { LatLng } from 'leaflet'
import { CanvasShip } from '@map-sdk/sdk/model/CanvasShip'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { CommUtils } from '@map-sdk/sdk/utils/CommUtils'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

interface CanvasShipSymbolOptions {
  color: string
  lineColor: string
  lineWidth: number
  heightLineColor: string
  isSelected?: boolean
  maptype?: any // 根据实际情况定义maptype类型
  sizeZoom: number
  minSilouetteZoom: number
  labelImages?: Record<string, unknown> // 或者定义更具体的类型
  radarOptions: {
    color: string
    lineColor: string
    heightLineColor: string
    lineWidth: number
    iconSize: number
  }
}

interface RadarDrawingOptions {
  fillStyle: string
  strokeStyle: string
  lineWidth: number
}

interface ArcDefinition {
  name: 'arc'
  area: [number, number][]
  centerXY: [number, number]
  headXY: [number, number]
  headXY2: [number, number]
  headLength: number
  radius: number
}
interface CalculatedLabelInfo {
  center: L.Point
  l: L.Point
  r: L.Point
  x: L.Point
  position: number
}

interface LabelImage {
  isDraw: boolean
  imgSize: number
  imgWidth: number
  imgOffset: number
  imgTypes: ('monitor' | 'monitor_nosign' | 'satellite' | 'dplus' | 'beidou')[]
}

type Point = [number, number]

export interface Template {
  headXY: [number, number]
  centerXY: Point
  area: Point[] // 假设每个顶点都是一个Point类型数组
  radius?: number // 对于drawArc函数，假设模板对象有个可选的圆半径属性
  headLength?: number
  name: string
}

interface DrawResult {
  area: Point[]
  centerXY: Point
}

class CanvasShipSymbol extends L.Class {
  latlng: L.LatLng = new LatLng(0, 0)
  ctx!: CanvasRenderingContext2D
  options!: CanvasShipSymbolOptions
  shipid!: string
  mmsi!: string
  lat!: number
  lng!: number
  rotate!: number
  pointCenterGPS: L.Point = new L.Point(0, 0)
  latlngReal: L.LatLng = new LatLng(0, 0)
  pointCenterReal: L.Point = new L.Point(0, 0)
  templater!: Template
  _map!: MyMap
  _ship: CanvasShip = new CanvasShip()
  x!: number
  y!: number
  state!: number

  bodyArea: Point[] = []
  head_x!: number
  head_y!: number

  constructor(
    latlng: L.LatLng,
    map: L.Map,
    ship: CanvasShip,
    options?: Partial<CanvasShipSymbolOptions>
  ) {
    super()

    if (!ship) {
      console.error('ship is undefined')
      return
    }

    this.options = {
      color: '',
      lineColor: '',
      lineWidth: 1,
      heightLineColor: '#000000',
      isSelected: false,
      maptype: undefined,
      sizeZoom: 1,
      minSilouetteZoom: 13,
      labelImages: {},
      radarOptions: {
        color: '#ffa500',
        lineColor: '#000000',
        heightLineColor: '#000000',
        lineWidth: 1,
        iconSize: 8,
      },
    }

    this.latlng = latlng || (ship.getLatlng() as L.LatLng)

    if (!this.latlng) {
      console.error('latlng is undefined')
      return
    }

    this.latlng.lng += ship.offset

    if (map) {
      this._map = map as any
      const canvasElement = this._map.getPanes()
        .canvasShipsPane as HTMLCanvasElement
      this.ctx = canvasElement.getContext('2d') as CanvasRenderingContext2D
      // 使用Partial合并默认值和传入的options
      this.options = { ...this.options, ...options }

      this._ship = ship
      this.shipid = ship.shipid
      this.mmsi = ship.mmsi
      this.lat = this.latlng.lat
      this.lng = this.latlng.lng

      const defaultOptions: Partial<CanvasShipSymbolOptions> = {
        color: this.options.color || ship.color || '#d3d3d3',
        lineColor: this.options.lineColor || ship.lineColor || '#000000',
        heightLineColor:
          this.options.heightLineColor || ship.heightLineColor || '#000000',
        lineWidth: this.options.lineWidth || ship.lineWidth || 1,
        isSelected: this.options.isSelected || ship.isSelected || false,
        sizeZoom: this.options.sizeZoom || 1,
        minSilouetteZoom: this.options.minSilouetteZoom || 13,
      }

      this.options = { ...defaultOptions, ...this.options }

      this.templater = CanvasShipUtils.getShipTemplater(
        this._map,
        this._ship,
        this.options.minSilouetteZoom
      )

      this.templater = CanvasShipUtils.sizeZoomShipTemplater(
        this.templater,
        this.options.sizeZoom
      )

      const hdg = 511 === this._ship.hdg_source ? 511 : this._ship.hdg || 0
      this.rotate = CanvasShipUtils.getAngle(
        hdg,
        this._ship.sog || 0,
        this._ship.cog || 0
      )

      const containerPoint = this._map.latLngToContainerPoint(this.latlng)
      this.pointCenterGPS = containerPoint
      this.latlngReal = latlng
      this.pointCenterReal = containerPoint

      if (this.templater && this.templater.name === 'nine') {
        const meters2pixel = CanvasShipUtils.meters2pixel(this._map)
        const shipRealCenterPoint = CanvasShipUtils.getShipRealCenterPoint(
          new L.Point(containerPoint.x, containerPoint.y),
          this._ship,
          meters2pixel
        )
        this.pointCenterReal = shipRealCenterPoint
        this.latlngReal = this._map.containerPointToLatLng(
          new L.Point(this.pointCenterReal.x, this.pointCenterReal.y)
        )
      }

      this.x = this.pointCenterReal.x || 0
      this.y = this.pointCenterReal.y || 0
      this.drawShipTemplater()
    } else {
      console.error('map is undefined')
    }
  }
  drawShipTemplater() {
    this._ship.is_radar
      ? this._ship.mmsi + !'0' && this._ship.radarid
        ? this.drawShipRadar(true)
        : (this.drawShip(this.templater), this.drawShipRadar(false))
      : this.drawShip(this.templater)
  }
  /**
   * 绘制雷达监测范围小红点 在船舶中间
   * @param drawDetails
   * @returns
   */
  drawShipRadar(drawDetails: boolean): this {
    const heightLineColor = this.options.radarOptions.heightLineColor
    const rotation = this.rotate || 0
    const centerPosition: Point = [this.x, this.y]
    const iconHalfSize = this.options.radarOptions.iconSize / 2
    const arcParams: ArcDefinition = {
      name: 'arc',
      area: [
        [0, iconHalfSize],
        [iconHalfSize, 0],
        [2 * iconHalfSize, iconHalfSize],
        [iconHalfSize, 2 * iconHalfSize],
      ],
      centerXY: [iconHalfSize, iconHalfSize],
      headXY: [iconHalfSize, 0],
      headXY2: [iconHalfSize, -10],
      headLength: 10,
      radius: iconHalfSize,
    }
    const drawingStyles: RadarDrawingOptions = {
      fillStyle: this.options.radarOptions.color,
      strokeStyle: this.options.radarOptions.lineColor,
      lineWidth: this.options.radarOptions.lineWidth || 1,
    }

    const arcResult = this.drawArc(
      this.ctx,
      centerPosition,
      arcParams,
      drawingStyles
    )
    if (arcResult) this.bodyArea = arcResult.area

    if (drawDetails) {
      const dx = centerPosition[0] - arcParams.centerXY[0]
      const dy = centerPosition[1] - arcParams.centerXY[1]
      const rotatedHeadPosition = CanvasShipUtils.pointRotate(
        centerPosition,
        [arcParams.headXY[0] + dx, arcParams.headXY[1] + dy],
        rotation
      )

      this.head_x = rotatedHeadPosition[0]
      this.head_y = rotatedHeadPosition[1]

      this._drawHeading(this.ctx, arcParams, heightLineColor)
      if (this.options.isSelected) {
        this._drawShipPos(this.ctx, arcParams, centerPosition, this.bodyArea)
      }
      this._drawTracks(this.ctx)
    }

    return this
  }
  drawShip(template: Template): this {
    this.bodyArea = []
    this.head_x = 0
    this.head_y = 0

    const rotate = this.rotate || 0
    const centerXY: [number, number] = [this.x, this.y]
    const drawingOptions: RadarDrawingOptions = {
      fillStyle: this.options.color,
      strokeStyle: this.options.lineColor,
      lineWidth: this.options.lineWidth,
    }

    const drawnPolygonInfo = this.drawPolygon(
      this.ctx,
      centerXY,
      rotate,
      template,
      drawingOptions
    )
    if (drawnPolygonInfo) this.bodyArea = drawnPolygonInfo.area

    const offsetCenterX = centerXY[0] - template.centerXY[0]
    const offsetCenterY = centerXY[1] - template.centerXY[1]
    const rotatedHeadPosition = CanvasShipUtils.pointRotate(
      centerXY,
      [template.headXY[0] + offsetCenterX, template.headXY[1] + offsetCenterY],
      rotate
    )

    this.head_x = rotatedHeadPosition[0]
    this.head_y = rotatedHeadPosition[1]

    this._drawHeading(this.ctx, template, this.options.heightLineColor)

    if (this.options.isSelected) {
      this._drawShipPos(this.ctx, template, centerXY, this.bodyArea)
    }

    this._drawTracks(this.ctx)

    return this
  }
  /**
   * 绘制标签入口方法初始化canvas等
   * @param calcLabel
   * @param labelImage
   * @param labelText
   * @param txtColor
   * @param font
   * @param lableRotate
   * @param lineColor
   * @param lineFillColor
   * @param obliqueLineColor
   * @returns
   */
  drawLable(
    calcLabel: CalculatedLabelInfo,
    labelImage: LabelImage,
    labelText: string,
    txtColor: string,
    font: string,
    lableRotate: number,
    lineColor: string,
    lineFillColor: string,
    obliqueLineColor: string
  ) {
    if (!calcLabel) {
      console.error('drawLable error calcLabel is null.')
      return
    }

    const canvasElement = this._map.getPanes()
      .canvasShipsPane as HTMLCanvasElement
    const canvas = canvasElement.getContext('2d') as CanvasRenderingContext2D

    this._drawLableCtx(
      canvas,
      labelImage.isDraw,
      labelImage,
      calcLabel,
      labelText,
      txtColor,
      font,
      lableRotate,
      lineColor,
      lineFillColor,
      obliqueLineColor
    )
  }

  /**
   * 绘制标签
   * @param canvas
   * @param isDraw
   * @param labelImage
   * @param calcLabel
   * @param labelText
   * @param txtColor
   * @param font
   * @param lableRotate
   * @param lineColor
   * @param lineFillColor
   * @param obliqueLineColor
   */
  private _drawLableCtx(
    canvas: CanvasRenderingContext2D,
    isDraw: boolean,
    labelImage: LabelImage,
    calcLabel: CalculatedLabelInfo,
    labelText: string,
    txtColor: string,
    font: string,
    lableRotate: number,
    lineColor: string,
    lineFillColor: string,
    obliqueLineColor: string
  ): void {
    const imageSize = labelImage.imgSize
    const halfImageSize = imageSize / 2
    const imgOffset = labelImage.imgOffset
    let textOffsetX = 0
    let iconOffsetY = 0
    const totalIconWidth = imageSize + 2 * imgOffset
    const imgTypes = labelImage.imgTypes
    const imgTypesLength = imgTypes.length

    canvas.save()

    if (lableRotate) {
      canvas.translate(calcLabel.center.x, calcLabel.center.y)
      canvas.rotate((lableRotate * Math.PI) / 180)
      canvas.translate(-calcLabel.center.x, -calcLabel.center.y)
    }

    if (isDraw) {
      textOffsetX = halfImageSize
    }

    if (labelText && labelText.length > 0) {
      const textXOffset = isDraw ? labelImage.imgWidth : 0
      const rectWidth = calcLabel.r.x - calcLabel.l.x
      CanvasShipUtils.drawRectangleRadius(
        canvas,
        calcLabel.l.x,
        calcLabel.l.y,
        rectWidth + 2 * imgOffset,
        totalIconWidth,
        lineColor,
        lineFillColor
      )
      this._drawText(
        canvas,
        new L.Point(
          calcLabel.l.x + textXOffset + imgOffset,
          calcLabel.l.y + 0.75 * totalIconWidth
        ),
        labelText,
        txtColor,
        font
      )

      let extraOffsetY = 0
      switch (calcLabel.position) {
        case 2:
        case 3:
        case 4:
          extraOffsetY = -totalIconWidth / 4
          break
        case 6:
        case 7:
          extraOffsetY = totalIconWidth / 4
          break
      }
      iconOffsetY = extraOffsetY
    }

    if (isDraw) {
      for (let i = 0; i < imgTypesLength; i++) {
        const currentImgX =
          imgOffset + halfImageSize + i * (imageSize + imgOffset)
        let iconName = ''
        switch (imgTypes[i]) {
          case 'monitor':
            this._drawLabelImageMonitor(
              canvas,
              calcLabel.l.x + currentImgX,
              calcLabel.l.y + 2 * textOffsetX - 4,
              halfImageSize
            )
            break
          case 'monitor_nosign':
            this._drawLabelImageMonitor(
              canvas,
              calcLabel.l.x + currentImgX,
              calcLabel.l.y + 2 * textOffsetX - 4,
              halfImageSize,
              true
            )
            break
          default:
            switch (imgTypes[i]) {
              case 'satellite':
                iconName = 'S'
                break
              case 'dplus':
                iconName = 'D'
                break
              case 'beidou':
                iconName = '斗'
            }
            this._drawIcon(
              canvas,
              calcLabel.l.x + currentImgX,
              calcLabel.l.y + 2 * textOffsetX - 4, //+ iconOffsetY,
              halfImageSize,
              iconName
            )
        }
      }
    }

    this._drawLine(
      canvas,
      new L.Point(calcLabel.center.x, calcLabel.center.y),
      new L.Point(calcLabel.x.x, calcLabel.x.y + iconOffsetY),
      obliqueLineColor
    )
    canvas.restore()
  }
  /**
   * 绘制标签图标
   * @param canvas
   * @param x
   * @param y
   * @param radius
   * @param text
   */
  private _drawIcon(
    canvas: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    text: string
  ) {
    const drawText = CommUtils.isHasZH_cn(text)

    canvas.save()
    canvas.beginPath()
    const fillStyle = canvas.createRadialGradient(
      x,
      y - radius / 2,
      radius / 50,
      x,
      y - radius / 2,
      0.6 * radius
    )
    fillStyle.addColorStop(0.5, '#ED5835')
    fillStyle.addColorStop(1, '#ED5835')
    canvas.arc(x, y, radius, 0, 360)
    canvas.fillStyle = fillStyle
    canvas.fill()
    canvas.closePath()
    canvas.restore()

    canvas.save()
    canvas.beginPath()
    const fontSize = 1.5 * radius
    canvas.font = `bold ${fontSize}px Arial`
    canvas.fillStyle = '#fff'
    let textXOffset = x - 0.35 * fontSize
    drawText && (textXOffset = x - 0.5 * fontSize)
    canvas.fillText(text, textXOffset, y + 0.35 * fontSize)
    canvas.fill()
    canvas.closePath()
    canvas.restore()
  }

  _drawLabelImageMonitor(
    canvas: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    hasSignature?: boolean
  ) {
    canvas.save()
    canvas.beginPath()
    const fillStyle = canvas.createRadialGradient(
      x,
      y - radius / 2,
      radius / 50,
      x,
      y - radius / 2,
      0.6 * radius
    )
    const color = '#ED5835'
    fillStyle.addColorStop(0.5, color)
    fillStyle.addColorStop(1, color)
    canvas.arc(x, y, radius, 0, 360)
    canvas.fillStyle = fillStyle
    canvas.fill()
    canvas.closePath()
    canvas.restore()

    canvas.save()
    canvas.beginPath()

    const start = [x - radius / 2, y - radius / 10]
    const end = [start[0], start[1] + radius / 1.5]
    const middle = [start[0], start[1] + (end[1] - start[1]) / 2]
    const topRight = [middle[0] + radius / 3, middle[1]]
    const center = [x, y]
    const bottomLeft = [start[0], start[1] - radius / 3]
    const bottomRight = [topRight[0], topRight[1] - 0.9 * radius]
    const rightMid = [topRight[0] + 0.8 * radius, center[1]]
    const midRight = [topRight[0] + radius / 2, topRight[1]]

    canvas.moveTo(start[0], start[1])
    canvas.lineTo(start[0], start[1])
    canvas.lineTo(end[0], end[1])
    canvas.moveTo(middle[0], middle[1])
    canvas.lineTo(middle[0], middle[1])
    canvas.lineTo(topRight[0], topRight[1])
    canvas.lineTo(center[0], center[1])
    canvas.moveTo(bottomLeft[0], bottomLeft[1])
    canvas.lineTo(bottomLeft[0], bottomLeft[1])
    canvas.lineTo(bottomRight[0], bottomRight[1])
    canvas.lineTo(rightMid[0], rightMid[1])
    canvas.lineTo(midRight[0], midRight[1])
    canvas.lineTo(bottomLeft[0], bottomLeft[1])

    canvas.strokeStyle = '#fff'
    canvas.lineWidth = 1.5
    canvas.lineJoin = 'round'
    canvas.stroke()
    canvas.closePath()
    canvas.restore()

    if (hasSignature) {
      const signatureStart = [start[0] - 4, start[1] + 5]
      const signatureEnd = [rightMid[0] + 1, rightMid[1] - 7]

      canvas.save()
      canvas.beginPath()
      canvas.moveTo(signatureStart[0], signatureStart[1])
      canvas.lineTo(signatureEnd[0], signatureEnd[1])
      canvas.strokeStyle = '#fff'
      canvas.lineWidth = 1
      canvas.lineJoin = 'round'
      canvas.stroke()
      canvas.closePath()
      canvas.restore()
    }
  }
  /**
   * 绘制标签图标
   * @param canvas
   * @param imageName
   * @param x
   * @param y
   * @param width
   * @param height
   */
  _drawLabelImage(
    canvas: CanvasRenderingContext2D,
    imageName: string,
    x: number,
    y: number,
    width: number,
    height: number
  ) {
    const image = new Image()
    image.onload = () => {
      canvas.save()
      canvas.drawImage(image, x - width / 2, y - height / 2, width, height)
      canvas.restore()
    }
    image.src = this.options.labelImages?.[imageName] as string
  }

  /**
   * 绘制标签文字
   * @param canvas
   * @param point
   * @param text
   * @param fillStyle
   * @param font
   */
  _drawText(
    canvas: CanvasRenderingContext2D,
    point: L.Point,
    text: string,
    fillStyle = '#000000',
    font = '600 12px Arial'
  ) {
    canvas.save()
    canvas.beginPath()
    canvas.font = font
    canvas.fillStyle = fillStyle
    canvas.fillText(text, point.x, point.y)
    canvas.fill()
    canvas.closePath()
    canvas.restore()
  }

  /**
   * 绘制标签指向线
   * @param canvas
   * @param startPoint
   * @param endPoint
   * @param color
   */
  _drawLine(
    canvas: CanvasRenderingContext2D,
    startPoint: L.Point,
    endPoint: L.Point,
    color = '#000000'
  ) {
    canvas.save()
    canvas.beginPath()
    canvas.moveTo(startPoint.x, startPoint.y)
    canvas.lineTo(endPoint.x, endPoint.y)
    canvas.strokeStyle = color
    canvas.lineWidth = 1
    canvas.lineJoin = 'round'
    canvas.stroke()
    canvas.closePath()
    canvas.restore()
  }
  /**
   * 绘制船舶指向线
   * @param canvas
   * @param template
   * @param strokeStyle
   */
  _drawHeading(
    canvas: CanvasRenderingContext2D,
    template: Template,
    strokeStyle: string
  ) {
    let headLength = template.headLength || 15
    let offset = 5
    if (template.name === 'nine') {
      headLength = 50
      offset = 10
    }

    if (this._ship.sog && this._ship.sog > 1) {
      canvas.save()
      canvas.beginPath()
      canvas.moveTo(this.head_x, this.head_y)

      const rotatedEndPoint = CanvasShipUtils.pointRotate(
        [this.head_x, this.head_y],
        [this.head_x, this.head_y - headLength],
        this.rotate
      )
      canvas.lineTo(rotatedEndPoint[0], rotatedEndPoint[1])

      if (this._ship.rot && this._ship.rot !== 0) {
        const rotationAngle = this._ship.rot > 0 ? 90 : -90
        canvas.moveTo(rotatedEndPoint[0], rotatedEndPoint[1])
        const baseRotatedPoint = CanvasShipUtils.pointRotate(
          [this.head_x, this.head_y],
          [this.head_x, this.head_y - headLength - offset],
          this.rotate
        )
        const finalRotatedPoint = CanvasShipUtils.pointRotate(
          rotatedEndPoint,
          baseRotatedPoint,
          rotationAngle
        )
        canvas.lineTo(finalRotatedPoint[0], finalRotatedPoint[1])
      }

      canvas.strokeStyle = strokeStyle
      canvas.lineWidth = 1
      canvas.lineJoin = 'round'
      canvas.stroke()
      canvas.closePath()
      canvas.restore()
    }
  }

  /**
   * 绘制选中船舶对象
   * @param canvas
   * @param shipType
   * @param position
   * @param areaLatLng
   */
  _drawShipPos(
    canvas: CanvasRenderingContext2D,
    shipType: Template,
    position: [number, number],
    areaLatLng: Point[]
  ) {
    canvas.save()
    canvas.beginPath()
    let minX = 0
    let minY = 0
    let maxX = 0
    let maxY = 0
    const boundingBox = CanvasShipUtils.getBoundingBoxArea(areaLatLng)
    if (boundingBox && boundingBox.min) {
      minX = boundingBox.min.x
      minY = boundingBox.min.y
    }
    if (boundingBox && boundingBox.max) {
      maxX = boundingBox.max.x
      maxY = boundingBox.max.y
    }

    if (shipType && shipType.name === 'three') {
      minX = position[0] - 8
      minY = position[1] - 8
      maxX = position[0] + 8
      maxY = position[1] + 8
    }

    canvas.moveTo(minX - 8, minY + 8 - 6)
    canvas.lineTo(minX - 8, minY - 8)
    canvas.moveTo(minX - 8, minY - 8)
    canvas.lineTo(minX + 8 - 6, minY - 8)
    canvas.moveTo(maxX - 8 + 6, minY - 8)
    canvas.lineTo(maxX + 8, minY - 8)
    canvas.moveTo(maxX + 8, minY - 8)
    canvas.lineTo(maxX + 8, minY + 8 - 6)
    canvas.moveTo(maxX + 8, maxY - 8 + 6)
    canvas.lineTo(maxX + 8, maxY + 8)
    canvas.moveTo(maxX + 8, maxY + 8)
    canvas.lineTo(maxX - 8 + 6, maxY + 8)
    canvas.moveTo(minX + 8 - 6, maxY + 8)
    canvas.lineTo(minX - 8, maxY + 8)
    canvas.moveTo(minX - 8, maxY + 8)
    canvas.lineTo(minX - 8, maxY - 8 + 6)
    canvas.closePath()
    canvas.strokeStyle = 'red'
    canvas.lineWidth = 1
    canvas.lineJoin = 'round'
    canvas.stroke()
    canvas.restore()
  }

  /**
   * 绘制最近轨迹
   * @param canvas
   */
  _drawTracks(canvas: CanvasRenderingContext2D) {
    if (this._ship.tracks && this._ship.tracks.length > 1) {
      canvas.save()
      canvas.closePath()
      canvas.beginPath()

      const { lat, lng } = this._ship.getLatlng()
      const currentPoint = CanvasShipUtils.GPSEncryptByMapTypeToPoint(
        L.latLng(lat, lng + this._ship.offset),
        this._map.mapType as string,
        this._map
      )

      canvas.moveTo(this.pointCenterReal.x, this.pointCenterReal.y)
      canvas.lineTo(currentPoint.x, currentPoint.y)
      canvas.moveTo(currentPoint.x, currentPoint.y)

      for (let i = this._ship.tracks.length - 1; i >= 0; i--) {
        const trackPoint = CanvasShipUtils.GPSEncryptByMapTypeToPoint(
          new LatLng(
            (this._ship.tracks[i] as any).lat,
            (this._ship.tracks[i] as any).lng + this._ship.offset
          ),
          this._map.mapType as string,
          this._map
        )
        canvas.lineTo(trackPoint.x, trackPoint.y)
        if (this._map.getZoom() >= 16) {
          canvas.arc(trackPoint.x, trackPoint.y, 1, 0, 360)
        }
        canvas.moveTo(trackPoint.x, trackPoint.y)
      }

      canvas.fillStyle = '#8d8d8d'
      canvas.fill()
      canvas.strokeStyle = '#8d8d8d'
      canvas.lineWidth = 1
      canvas.lineJoin = 'round'
      canvas.stroke()
      canvas.closePath()
      canvas.restore()
    }
  }

  /**
   * 绘制船舶符号
   * @param canvas
   * @param centerXY
   * @param rotate
   * @param templater
   * @param options
   * @returns
   */
  drawPolygon(
    canvas: CanvasRenderingContext2D,
    centerXY: Point,
    rotate: number,
    templater: Template,
    options?: {
      fillStyle?: string
      strokeStyle?: string
      lineWidth?: number
    }
  ): DrawResult | undefined {
    if (!canvas || !centerXY || !templater) return

    const defaultOptions: {
      fillStyle: string
      strokeStyle: string
      lineWidth: number
    } = {
      fillStyle: '#ffa500',
      strokeStyle: '#000000',
      lineWidth: 1,
    }

    const combinedOptions = { ...defaultOptions, ...options }

    canvas.save()
    canvas.beginPath()

    const offsetX = centerXY[0] - templater.centerXY[0]
    const offsetY = centerXY[1] - templater.centerXY[1]

    const result: DrawResult = {
      area: [],
      centerXY,
    }

    const pointRotate = CanvasShipUtils.pointRotate(
      centerXY,
      [templater.area[0][0] + offsetX, templater.area[0][1] + offsetY],
      rotate
    )
    canvas.moveTo(pointRotate[0], pointRotate[1])

    for (let i = 0; i < templater.area.length; i++) {
      const shiftedX = templater.area[i][0] + offsetX
      const shiftedY = templater.area[i][1] + offsetY
      const rotatedPoint = CanvasShipUtils.pointRotate(
        centerXY,
        [shiftedX, shiftedY],
        rotate
      ) as Point
      canvas.lineTo(rotatedPoint[0], rotatedPoint[1])
      result.area.push(rotatedPoint)
    }

    if (combinedOptions.fillStyle) {
      canvas.fillStyle = combinedOptions.fillStyle
      canvas.fill()
    }

    if (combinedOptions.strokeStyle) {
      canvas.strokeStyle = combinedOptions.strokeStyle
      canvas.lineWidth = combinedOptions.lineWidth
      canvas.lineJoin = 'round'
      canvas.lineCap = 'round' // 更正了拼写错误，应该是lineCap不是lineCop
      canvas.stroke()
    }

    canvas.closePath()
    canvas.restore()

    return result
  }

  drawArc(
    canvas: CanvasRenderingContext2D,
    point: Point,
    arcTemplate: Pick<Template, 'centerXY' | 'area' | 'radius'>,
    options?: {
      fillStyle?: string
      strokeStyle?: string
      lineWidth?: number
    }
  ): DrawResult | undefined {
    if (!canvas || !point || !arcTemplate.radius) return

    const defaultOptions: {
      fillStyle: string
      strokeStyle: string
      lineWidth: number
    } = {
      fillStyle: '#ffa500',
      strokeStyle: '#000000',
      lineWidth: 1,
    }

    const combinedOptions = { ...defaultOptions, ...options }

    canvas.save()
    canvas.beginPath()

    const shiftedPoints: Point[] = []
    const dx = point[0] - arcTemplate.centerXY[0]
    const dy = point[1] - arcTemplate.centerXY[1]

    for (let i = 0; i < arcTemplate.area.length; i++) {
      const shiftedX = arcTemplate.area[i][0] + dx
      const shiftedY = arcTemplate.area[i][1] + dy
      shiftedPoints.push([shiftedX, shiftedY])
    }

    const result: DrawResult = {
      area: shiftedPoints,
      centerXY: point,
    }

    canvas.arc(point[0], point[1], arcTemplate.radius, 0, 360)

    if (combinedOptions.fillStyle) {
      canvas.fillStyle = combinedOptions.fillStyle
      canvas.fill()
    }

    if (combinedOptions.strokeStyle) {
      canvas.strokeStyle = combinedOptions.strokeStyle
      canvas.lineWidth = combinedOptions.lineWidth
      canvas.lineJoin = 'round'
      canvas.lineCap = 'round'
      canvas.stroke()
    }

    canvas.closePath()
    canvas.restore()

    return result
  }
}

const canvasShipSymbol = (
  canvas: L.LatLng,
  map: L.Map,
  ship: CanvasShip,
  options?: Partial<CanvasShipSymbolOptions>
) => {
  return new CanvasShipSymbol(canvas, map, ship, options)
}

export { canvasShipSymbol, CanvasShipSymbol }
